import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getStoredUser } from "@/lib/api";
import { Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export const PLANS = [
  {
    id: "trial",
    name: "Prueba",
    price: "Gratis",
    period: "14 días",
    tagline: "Para probarlo sin compromiso",
    cta: "Empezar gratis",
    features: [
      "1 negocio (plantilla a elegir)",
      "Equipo completo de agentes del sector",
      "1 usuario",
      "Subida de documentos",
      "Sin tarjeta para empezar",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: "29 €",
    period: "/mes",
    tagline: "Autónomos y micronegocios",
    cta: "Elegir Starter",
    features: [
      "1 negocio con todos sus agentes",
      "2 usuarios",
      "Chat ilimitado con tus agentes",
      "Documentos y búsqueda inteligente",
      "Auditoría y control de coste",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "79 €",
    period: "/mes",
    tagline: "Pymes en crecimiento",
    popular: true,
    cta: "Elegir Pro",
    features: [
      "Varios negocios (varias plantillas)",
      "Crea y personaliza tus propios agentes",
      "5 usuarios",
      "CRM, tareas y flujos de trabajo",
      "Automatizaciones y aprobaciones",
      "Todo lo de Starter",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: "199 €",
    period: "/mes",
    tagline: "Empresas y equipos",
    cta: "Elegir Business",
    features: [
      "Usuarios ilimitados",
      "Integraciones y acceso por API",
      "Soporte prioritario",
      "Tu marca (white-label)",
      "Todo lo de Pro",
    ],
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const user = getStoredUser();
  const [loading, setLoading] = useState(null);

  const choose = async (plan) => {
    if (!user) return nav("/register");          // primero hay que tener cuenta
    if (plan.id === "trial") return nav("/dashboard");
    setLoading(plan.id);
    try {
      const { data } = await api.post("/billing/checkout", { plan: plan.id });
      window.location.href = data.url;           // al checkout seguro de Stripe
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el pago");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="pricing-page">
      {/* Cabecera */}
      <div className="border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark className="w-8 h-8" />
            <div>
              <div className="font-display font-black text-[14px] tracking-tight leading-none">AGENT COMPANY OS</div>
              <div className="label-mono mt-1">por BeautyOS</div>
            </div>
          </div>
          <button
            onClick={() => nav(user ? "/dashboard" : "/login")}
            className="text-sm text-[var(--text-dim)] hover:text-white flex items-center gap-2"
            data-testid="pricing-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {user ? "Volver al panel" : "Iniciar sesión"}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-12">
          <div className="label-mono mb-3">PLANES Y PRECIOS</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4">
            Tu empresa con un equipo de IA
          </h1>
          <p className="text-[var(--text-dim)] max-w-2xl mx-auto">
            Elige tu sector y ten a tu disposición agentes especializados desde el primer minuto.
            Cancela cuando quieras.
          </p>
        </div>

        <div className="text-center mb-4">
          <div className="label-mono" style={{ color: "var(--accent)" }}>
            AUTOSERVICIO · TÚ LO MONTAS (TRAES TU CLAVE DE IA)
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className="relative border bg-[var(--surface)] p-6 flex flex-col"
              style={{ borderColor: p.popular ? "var(--accent)" : "var(--border)" }}
              data-testid={`plan-${p.id}`}
            >
              {p.popular && (
                <div
                  className="absolute -top-3 left-6 px-2 py-0.5 text-[10px] font-mono font-bold"
                  style={{ background: "var(--accent)", color: "var(--bg)" }}
                >
                  MÁS ELEGIDO
                </div>
              )}
              <div className="font-display font-bold text-xl mb-1">{p.name}</div>
              <div className="text-xs text-[var(--text-dim)] mb-5">{p.tagline}</div>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display font-black text-4xl tracking-tight">{p.price}</span>
                <span className="text-sm text-[var(--text-dim)]">{p.period}</span>
              </div>

              <ul className="space-y-2.5 flex-1 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs leading-relaxed">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                    <span className="text-[var(--text)]">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => choose(p)}
                disabled={loading === p.id}
                className="w-full py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
                style={
                  p.popular
                    ? { background: "var(--accent)", color: "var(--bg)" }
                    : { border: "1px solid var(--border)", color: "var(--text)" }
                }
                data-testid={`plan-cta-${p.id}`}
              >
                {loading === p.id ? "Redirigiendo a Stripe…" : p.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Nota BYOK */}
        <div className="mt-12 border border-[var(--border)] bg-[var(--surface)] p-6 max-w-3xl mx-auto text-center">
          <div className="label-mono mb-2" style={{ color: "var(--accent)" }}>SIN SORPRESAS EN LA FACTURA</div>
          <p className="text-sm text-[var(--text-dim)] leading-relaxed">
            El precio del plan es solo por el software. La inteligencia artificial la pones tú con tu propia
            clave (Anthropic, OpenAI o Google): pagas su consumo directamente a tu proveedor, al coste, sin
            recargo por nuestra parte. Tú controlas el gasto en todo momento desde <strong>Coste de IA</strong>.
          </p>
        </div>

        {/* Separador */}
        <div className="text-center my-10 label-mono text-[var(--text-dim)]">— O SI LO PREFIERES —</div>

        {/* Servicio gestionado / hecho por ti */}
        <div
          className="border-2 p-6 md:p-8 max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-6"
          style={{ borderColor: "var(--accent)", background: "var(--surface)" }}
          data-testid="managed-service"
        >
          <div className="flex-1">
            <div className="label-mono mb-2" style={{ color: "var(--accent)" }}>HECHO POR TI · SERVICIO GESTIONADO</div>
            <h3 className="font-display font-bold text-2xl mb-2">¿No quieres liarte con claves ni ajustes?</h3>
            <p className="text-sm text-[var(--text-dim)] leading-relaxed">
              Lo montamos por ti: configuramos tus agentes con la información de tu negocio, conectamos
              tus canales y lo dejamos funcionando. Tú te olvidas de la parte técnica y te centras en vender.
            </p>
          </div>
          <div className="text-center shrink-0">
            <div className="flex items-baseline gap-1 justify-center mb-3">
              <span className="text-xs text-[var(--text-dim)]">desde</span>
              <span className="font-display font-black text-4xl tracking-tight">300 €</span>
              <span className="text-sm text-[var(--text-dim)]">/mes</span>
            </div>
            <button
              onClick={() => nav("/servicio")}
              className="px-6 py-2.5 text-sm font-semibold"
              style={{ background: "var(--accent)", color: "var(--bg)" }}
              data-testid="managed-cta"
            >
              Solicitar servicio gestionado
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-[var(--text-dim)] mt-8">
          Precios en euros, IVA no incluido. Pago mensual, cancelable en cualquier momento.
        </p>
      </div>
    </div>
  );
}
